import React, {useEffect, useRef} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import cloudTopLeft from "../assets/images/mobile/HomeCloudLeft.png";
import cloudBottomRight from "../assets/images/mobile/HomeCloudRight.png";
import MobileHomeImage from "../assets/images/mobile/MobileHomeIcon.png";
import arrow from "../assets/images/mobile/prototype/arrow.svg";
import {FarewiseIcon} from "../assets/svgComponents/farewiseIcon";
import {PlaneIcon} from "../assets/svgComponents/PlaneIcon";
import {useTranslation} from "../i18n/LanguageProvider";
import {FlightSearchData} from "../models/flightSearchData";
import {SelectorType} from "../models/FlightSearch";
import FlightSearchSelection from "../components/selector/FlightSearchSelection";
import {useFlightSearchStore} from "../store/useFlightSearchStore";
import DiscoveryLinks from "../components/search/DiscoveryLinks";

interface Props {
    flightSearchData: FlightSearchData;
    setFlightSearchData: React.Dispatch<React.SetStateAction<FlightSearchData>>;
}

const steps = [SelectorType.Airport, SelectorType.Dates, SelectorType.Category];

export default function MobileHomePage({flightSearchData, setFlightSearchData}: Props) {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const stepValue = Number(params.get("step"));
    const step = [1, 2, 3].includes(stepValue) ? stepValue : 0;
    const headingRef = useRef<HTMLHeadingElement>(null);
    const setData = useFlightSearchStore((state) => state.setData);

    useEffect(() => {
        setData(flightSearchData);
    }, [flightSearchData, setData]);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (step) headingRef.current?.focus({preventScroll: true});
    }, [step]);

    const goToStep = (nextStep: number) => setParams(nextStep ? {step: String(nextStep)} : {});
    const canContinue = step === 1
        ? flightSearchData.airports.length > 0
        : step === 2
            ? !!flightSearchData.dateFrom && !!flightSearchData.dateTo
            : flightSearchData.categories.length > 0;
    const search = () => {
        setData(flightSearchData);
        navigate("/search");
    };
    const selection = (selector: SelectorType) => (
        <FlightSearchSelection
            activeSelector={selector}
            setActiveSelector={() => undefined}
            flightSearchData={flightSearchData}
            setFlightSearchData={setFlightSearchData}
            embedded
        />
    );

    return (
        <main className={`mobile-prototype ${step ? "mobile-wizard" : "mobile-welcome"}`}>
            {!step && <>
                <img src={cloudTopLeft} alt="" className="welcome-cloud welcome-cloud-left" />
                <img src={cloudBottomRight} alt="" className="welcome-cloud welcome-cloud-right" />
            </>}
            <div className="mobile-prototype-inner">
                <header className="mobile-prototype-header">
                    {step > 0 && (
                        <nav className="mobile-progress" aria-label={t("mobile.steps")}>
                            {steps.map((_, index) => (
                                <span key={index} aria-current={step === index + 1 ? "step" : undefined}
                                    aria-label={t("mobile.stepCount", {step: index + 1})} />
                            ))}
                        </nav>
                    )}
                    <button type="button" className="mobile-brand" onClick={() => goToStep(0)} aria-label={t("home.aria")}>
                        <FarewiseIcon />
                    </button>
                </header>

                {!step ? (
                    <>
                        <div className="relative z-10 mb-5 flex justify-start"><DiscoveryLinks onNavigate={() => setData(flightSearchData)} /></div>
                        <h1 className="mobile-welcome-title">
                            {t("mobile.headlineLine1")}<br />{t("mobile.headlineLine2")}
                        </h1>
                        <img src={MobileHomeImage} alt="" className="mobile-welcome-art" />
                        <button type="button" onClick={() => goToStep(1)} className="mobile-primary mobile-discover">
                            <PlaneIcon />{t("mobile.discover")}
                        </button>
                    </>
                ) : (
                    <>
                        <h1 ref={headingRef} tabIndex={-1} className="mobile-step-title">
                            {t(`mobile.step${step}Title`)}
                        </h1>
                        <div className="mobile-step-content">
                            {selection(steps[step - 1])}
                            {step === 3 && (
                                <details className="mobile-destination-options">
                                    <summary>{t("mobile.refineDestination")}</summary>
                                    {selection(SelectorType.Destination)}
                                </details>
                            )}
                        </div>
                        <footer className="mobile-step-actions">
                            <button type="button" className="mobile-back" onClick={() => goToStep(step - 1)}>
                                {t("mobile.back")}
                            </button>
                            <button type="button" disabled={!canContinue} className="mobile-primary"
                                onClick={() => step < 3 ? goToStep(step + 1) : search()}>
                                {step < 3 ? t("mobile.next") : t("mobile.search")}
                                <img src={arrow} alt="" className="mobile-next-arrow" />
                            </button>
                        </footer>
                    </>
                )}
            </div>
        </main>
    );
}
